import {
  Alert,
  alertTypes,
  alertVariants,
  Modal,
  NewButton,
  NewButtonVariant,
  Radio,
} from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { exportShredAnnotatedDocument } from "slices/shredding/detail/actions";
import { getErrorMessage } from "utils";

import classes from "./ShredAnnotatedDocumentModal.module.scss";

const DEFAULT_AUTHOR_OPTION = "Use 'VisibleThread' as the comment author (default)";
const CATEGORY_AUTHOR_OPTION = "Use the dictionary category as the comment author";

const OPTIONS = [
  { text: DEFAULT_AUTHOR_OPTION, disabled: false },
  { text: CATEGORY_AUTHOR_OPTION, disabled: false },
];

const ShredAnnotatedDocumentModal = ({
  baseApiPath,
  reportId,
  criticalityFilters,
}) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState(DEFAULT_AUTHOR_OPTION);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setError(null);
    setIsGenerating(true);
    try {
      await dispatch(
        exportShredAnnotatedDocument({
          baseApiPath,
          reportId,
          criticalityFilters,
          categoryAsCommentAuthor: selectedOption === CATEGORY_AUTHOR_OPTION,
        })
      ).unwrap();
      dispatch(hideModal(modalTypes.SHRED_ANNOTATED_DOCUMENT));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCancel = () => {
    if (isGenerating) {
      return;
    }
    dispatch(hideModal(modalTypes.SHRED_ANNOTATED_DOCUMENT));
  };

  return (
    <Modal
      data-test="shredAnnotatedDocumentModal"
      className="fade-in"
      show
      backdrop
      centered
      onDismiss={handleCancel}
    >
      <Modal.Header>
        <b className={classes.header}>Download Annotated Document</b>
      </Modal.Header>
      <Modal.Body>
        <div className={classes.body}>
          Annotated Document Settings:
          <Radio
            options={OPTIONS}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            disabled={isGenerating}
          />
          {error ? (
            <Alert
              variant={alertVariants.danger}
              text={error}
              type={alertTypes.inline}
              hideCloseButton
            />
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
          disabled={isGenerating}
        >
          Cancel
        </NewButton>
        <NewButton
          data-testid="generate-document-button"
          variant={NewButtonVariant.l1}
          onClick={handleSubmit}
          isLoading={isGenerating}
          disabled={isGenerating}
        >
          Generate Document
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

ShredAnnotatedDocumentModal.propTypes = {
  baseApiPath: PropTypes.string.isRequired,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  criticalityFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ShredAnnotatedDocumentModal;
